/**
 * English UI strings. This is the reference locale: every other locale must
 * provide the same keys (see `Translations` in ./index.tsx).
 *
 * `{placeholders}` are replaced at render time.
 */
export const en = {
  'app.name': 'FilmSloth',
  'app.tagline': 'Decide together what to watch tonight. No rush.',

  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.continue': 'Continue',
  'common.loading': 'Loading…',
  'common.retry': 'Try again',
  'common.close': 'Close',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.minutes': '{count} min',
  'common.host': 'Host',
  'common.you': 'you',
  'common.language': 'Language',
  'common.anyGenre': 'Any genre',
  'common.withoutGenres': 'No {genres}',
  'common.anyYear': 'Any year',
  'common.fromYear': 'From {year}',
  'common.anyRating': 'Any rating',
  'common.fromRating': '★ {rating}+',
  'common.noLimit': 'No limit',

  'home.create': 'Start a session',
  'home.createHint': 'Pick the services, invite your friends, start swiping.',
  'home.join': 'Join a session',
  'home.joinHint': 'Got a code from a friend? Enter it here.',

  'create.title': 'New session',
  'create.yourName': 'Your name',
  'create.namePlaceholder': 'e.g. Alex',
  'create.providers': 'Streaming services',
  'create.providersHint': 'Only titles on these services will show up.',
  'create.genres': 'Genres',
  'create.genresHint': 'Leave empty for anything.',
  'create.excludedGenres': 'Leave out',
  'create.excludedGenresHint': 'Anything with one of these genres stays out of the deck.',
  'create.runtime': 'Maximum runtime',
  'create.minYear': 'Released',
  'create.tryWithoutYear': 'Without the year limit there would be {count}.',
  'create.minRating': 'TMDB rating',
  'create.minRatingHint':
    'Films with too few votes have no rating yet and stay out once a minimum is set.',
  'create.tryWithoutRating': 'Without the minimum rating there would be {count}.',
  'create.submit': 'Create session',
  'create.needName': 'Enter your name to continue.',
  'create.needProvider': 'Pick at least one streaming service.',
  'create.matchingMovies': '{count} movies match',
  'create.noMovies': 'No movies match these filters yet.',
  'create.tryWithoutGenres': 'Without the genre filter there would be {count}.',
  'create.tryWithoutRuntime': 'Without the length limit there would be {count}.',
  'create.tryMoreProviders': 'Try adding another streaming service.',

  'join.title': 'Join a session',
  'join.code': 'Session code',
  'join.codePlaceholder': 'ABC123',
  'join.yourName': 'Your name',
  'join.submit': 'Join',
  'join.hostedBy': 'Hosted by {name}',
  'join.peopleInside': '{count} in the session',
  'join.notFound': 'No session with that code.',
  'join.scan': 'Scan the QR code',
  'join.scanHint': 'Point the camera at the code on the other phone.',
  'join.scanDenied': 'No camera here. Allow the camera, or type the code above.',
  'join.scanUnavailable': 'This browser cannot scan. Type the code above.',

  'lobby.title': 'Waiting room',
  'lobby.shareCode': 'Share this code',
  'lobby.shareLink': 'Copy link',
  'lobby.inviteLink': 'Invite link',
  'lobby.qrLabel': 'QR code for the invite link to session {code}',
  'lobby.qrHint': 'Anybody in the room can point their camera at this.',
  'lobby.copyFailed': 'Copying is blocked here — select the link above and copy it manually.',
  'lobby.localhostHint':
    'You opened the app on localhost, so this link only works on this computer. To invite people on your Wi-Fi, open the network address printed by the dev server instead.',
  'lobby.people': 'In the session',
  'lobby.filters': 'What you are picking from',
  'lobby.deckSize': '{count} movies in the deck',
  'lobby.start': 'Start swiping',
  'lobby.waitingForHost': 'Waiting for {name} to start…',
  'lobby.invite': 'Invite friends',
  'lobby.copyCode': 'Copy code',
  'lobby.inviteTitle': 'FilmSloth',
  'lobby.inviteText': "Join my FilmSloth session and help pick tonight's movie. Code: {code}",
  'lobby.aloneHint': 'Nobody else yet — you can still start, and people can join later.',
  'lobby.inviteMore': 'Invite more people',
  'lobby.editFilters': 'Change filters',

  'swipe.like': 'Yes',
  'swipe.neutral': "Don't mind",
  'swipe.dislike': 'No',
  'swipe.progress': '{voted} of {total} rated',
  'swipe.emptyTitle': 'That was everything',
  'swipe.emptyBody':
    'You have rated every movie left in the deck. Hang back — the others are still going.',
  'swipe.results': 'Results',
  'swipe.leave': 'Leave session',
  'swipe.finishSession': 'End session',
  'swipe.finishConfirm': 'Really end it for everyone?',
  'swipe.leaveShort': 'Leave',
  'swipe.leaveConfirm': 'Really leave?',
  'swipe.undo': 'Undo',
  'swipe.later': 'Ask me later',
  'swipe.alreadySeen': 'Already seen',
  'swipe.keyboardHint': 'Tip: swipe the card, or use ← ↓ → on your keyboard. ↑ asks again later.',

  'trailer.play': 'Play trailer',
  'trailer.label': 'Trailer for {title}',
  'trailer.consentNote':
    'Playing loads the video from YouTube, which sends your IP address to Google.',
  'trailer.disable': 'Turn trailers off',
  'trailer.community': 'Added by the community',

  // Rejecting a trailer. The wording avoids promising removal: one session
  // only ever hides it for itself, however the wider count comes out.
  'trailerReport.action': 'Wrong trailer?',
  'trailerReport.busy': 'Reporting…',
  'trailerReport.hidden': 'Hidden for this session. Thanks for the heads-up!',
  'trailerReport.counted':
    'Hidden here, and noted. If a few more groups say the same, it comes down for everyone.',
  'trailerReport.removed': 'Removed for everyone. Somebody can now add the right one.',
  'trailerReport.failed': 'That did not go through. Please try again in a little while.',

  // Proposing a trailer. Accepted submissions go live for everyone at once,
  // so the form says up front what will and will not be taken.
  'trailerAdd.open': 'Know the trailer? Add it',
  'trailerAdd.title': 'Add a trailer for {title}',
  'trailerAdd.hint':
    'Paste the YouTube link. We only accept trailers from official studio and distributor channels, and we check that the video really is the trailer for this film.',
  'trailerAdd.placeholder': 'https://www.youtube.com/watch?v=...',
  'trailerAdd.submit': 'Add trailer',
  'trailerAdd.submitting': 'Checking…',
  'trailerAdd.cancel': 'Cancel',
  'trailerAdd.success': 'Thanks! The trailer is now live for everyone in this session.',

  // One message per rejection the server can return, so somebody who pasted
  // the wrong thing knows what to paste instead.
  'trailerError.not_youtube':
    'That is not a YouTube link. Paste a link from youtube.com or youtu.be.',
  'trailerError.trailer_broken':
    'YouTube says that video is unavailable or cannot be embedded here. Try another link.',
  'trailerError.trailer_unverifiable':
    'We could not reach YouTube to check that video. Please try again in a moment.',
  'trailerError.channel_not_official':
    'That video is not from an official studio or distributor channel. We only take trailers from the studio itself, so we know they may be shown here.',
  'trailerError.not_a_trailer':
    'That video does not look like a trailer. Look for the official trailer or teaser.',
  'trailerError.title_mismatch': 'That trailer seems to be for a different film.',
  'trailerError.year_mismatch': 'That trailer seems to be for a different version of this film.',
  'trailerError.trailer_exists': 'Somebody just added a trailer for this film.',
  'trailerError.rate_limited':
    'Thanks — we really appreciate you helping with trailers! To keep things safe we limit how many changes can be made at once. Please try again a little later.',
  'trailerError.unknown': 'That trailer could not be added. Please try a different link.',

  'match.title': "It's a match!",
  'match.body': 'Everyone is fine with this one.',
  'match.hostWatch': 'Watch this',
  'match.hostContinue': 'Keep looking',
  'match.waitingForHost': '{name} is deciding whether to keep looking…',

  'results.title': 'Results so far',
  'results.subtitle': 'Like +1 · Don’t mind 0 · No −1',
  'results.empty': 'Nobody has rated anything yet.',
  'results.points': '{count} pts',
  'results.votes': '{likes} yes · {neutrals} don’t mind · {dislikes} no',
  'results.votersNo': 'No: {names}',
  'results.votersYes': 'Yes: {names}',
  'results.votersNeutral': 'Don’t mind: {names}',
  'results.matched': 'Matched',
  'results.complete': 'Everyone voted',

  'watchOn.title': 'Where to watch it',
  'watchOn.searchHint': 'Search {provider} for “{title}”',
  'watchOn.homeHint': 'Open {provider} — we have no search link for this one',
  'taste.favourites': 'Your group leans towards {genres}.',
  'taste.avoided': 'And steers clear of {genres}.',

  'ranking.share': 'Share the top five',
  'ranking.shareHeader': 'Our FilmSloth ranking:',

  'share.action': 'Share the pick',
  'share.copied': 'Copied to clipboard',
  'share.failed': 'Could not share',
  'share.subject': 'Tonight we are watching',
  'share.body': 'Tonight we are watching {title} ({year}). Picked with FilmSloth.',
  'share.bodyNoYear': 'Tonight we are watching {title}. Picked with FilmSloth.',

  'group.title': 'The group ({count})',
  'group.rated': '{count} rated',
  'group.ratedDone': '{count} rated · through the deck',
  'group.stuckHint':
    'A match needs a vote from everybody. If somebody has stopped swiping, the host can take them out.',
  'group.makeHost': 'Make host',
  'group.remove': 'Remove',
  'group.removeLabel': 'Remove {name} from the session',
  'group.removeConfirm': 'Remove {name}?',
  'group.removedBanner':
    'The host took you out of the session. The invite link still works if you want back in.',

  'matchRule.label': 'When is it a match?',
  'matchRule.lenient': 'Nobody minds',
  'matchRule.strict': 'Everybody wants it',
  'matchRule.lenientHint': 'Everyone has voted, nobody said no, and at least one said yes.',
  'matchRule.strictHint': 'Only when every single vote is a yes.',

  'timer.label': 'Time limit',
  'timer.enable': 'Give the round a time limit',
  'timer.minutesLabel': 'Minutes',
  'timer.minutesUnit': 'minutes',
  'timer.hint': 'When the time is up the shortlist decides — and you can always add more first.',
  'timer.summary': 'Time limit: {count} min',
  'timer.offSummary': 'No time limit',
  'timer.untilRunoff': 'left before the shortlist',
  'timer.upTitle': "Time's up",
  'timer.upBody': 'The shortlist vote starts in {time}.',
  'timer.upBodyNothing': 'Too little is voted on for a shortlist. Add time, or keep swiping.',
  'timer.upWaiting': '{name} decides whether to add time.',
  'timer.add': 'Add {count} min',
  'timer.addLabel': 'Minutes to add',
  'timer.off': 'Drop the time limit',

  'runoff.offer':
    'Every card is voted on and nothing matched. There is still a shortlist worth deciding between.',
  'runoff.offerWaiting': 'The host can put the shortlist to a vote.',
  'runoff.start': 'Put it to a vote',
  'runoff.title': 'Shortlist',
  'runoff.body': 'One vote each, and you can change it until the last person has voted.',
  'runoff.outstanding': 'Waiting for {count} more.',
  'runoff.allIn': 'Everybody has voted.',
  'runoff.tieHost': "It's a tie. You decide.",
  'runoff.tieWaiting': "It's a tie — the host decides.",
  'runoff.changeHint': 'Tap another title to change your vote.',
  'runoff.votes': '{count} votes',

  'error.offline': 'You are offline. Nothing will go through until the connection is back.',

  'finished.title': 'Tonight you are watching',
  'finished.noPick': 'The session was closed without a pick.',
  'finished.viewResults': 'See the full ranking',
  'finished.nextRound': 'Another round, same group',
  'finished.joinNextRound': 'Join the next round',
  'finished.nextRoundStarted': 'Somebody started another round with the same filters.',
  'finished.newSession': 'Start a new session',

  'finished.watchNow': 'Watch now on {provider}',
  'finished.alsoOn': 'Also on:',
  'finished.details': 'Details & trailer',

  'finished.tipHeadlineSaved': '{minutes} saved',
  'finished.tipHeadlineSlow': 'Took {duration}',
  'finished.donateBodySaved':
    'You picked in {duration}, well under the usual {average}. That is {minutes} back in your life.',
  'finished.donateBodySlow':
    'That took {duration}, a bit over the usual {average}. No judgment — deciding is hard.',
  'finished.tipLabel': 'Tip for the sloth butler',
  'finished.tipOther': 'Other amount',

  'error.generic': 'Something went wrong.',
  'error.connectionLost': 'Connection lost. Reconnecting…',
  'swipe.pendingVotes': 'Not sent yet: {count}. Goes out by itself once the connection is back.',
  'error.sessionGone': 'This session no longer exists.',

  'rating.label': 'Your rating',
  'rating.score': '{score} out of {max}',
  'rating.community': '{score} from {count} ratings here',
  'rating.provisional': 'still settling',
  'rating.none': 'Nobody here has rated this yet.',
  'rating.clear': 'Remove rating',
  'rating.prompt': 'How was it?',

  'watchlist.title': 'Your films',
  'watchlist.open': 'Your films',
  'watchlist.save': 'Save',
  'watchlist.saved': 'Saved',
  'watchlist.tabSaved': 'Kept for later',
  'watchlist.tabWatched': 'Watched together',
  'watchlist.emptySaved': 'Nothing kept yet. Tap the star on a card to hold on to a film.',
  'watchlist.emptyWatched': 'No film has won a session yet.',
  'watchlist.deviceNote': 'This list lives on this device. Clearing your cookies clears it.',

  'movieSuggestion.suggest': 'Suggest film',
  'movieSuggestion.suggested': 'Suggestion submitted',
  'movieSuggestion.title': 'Suggest a film',
  'movieSuggestion.hint':
    'Only enter what you know for sure. The film will appear immediately for everyone.',
  'movieSuggestion.movieName': 'Film name',
  'movieSuggestion.year': 'Release year',
  'movieSuggestion.runtimeHint': 'e.g. 136 for 2h 16min',
  'movieSuggestion.namePlaceholder': 'e.g. The Matrix',
  'movie.community': 'Suggested by the community',
  'movie.providersSource': 'Streaming data: JustWatch',
  'movie.ratingVotes': 'TMDB rating from {count} votes',
  'movieSuggestion.runtime': 'Duration (minutes)',
  'movieSuggestion.genres': 'Genres (optional)',
  'movieSuggestion.providers': 'Streaming providers (optional)',
  'movieSuggestion.submit': 'Submit suggestion',
  'movieSuggestion.submitting': 'Submitting…',
  'movieSuggestion.cancel': 'Cancel',
  'movieSuggestion.success': 'Film added!',

  // Auth: creating an account is optional almost everywhere. It only starts
  // paying off once you contribute to the workshop or want to go ad-free.
  'auth.title': 'Sign in or register',
  'auth.subtitle':
    'You only need an account to contribute in the workshop or go ad-free. Everything else works without one.',
  'auth.login': 'Log in',
  'auth.register': 'Register',
  'auth.email': 'Email',
  'auth.emailOrUsername': 'Email or username',
  'auth.username': 'Username',
  'auth.usernameHint': '3–32 characters: letters, numbers, “_” and “-”.',
  'auth.password': 'Password',
  'auth.submitLogin': 'Log in',
  'auth.submitRegister': 'Create account',
  'auth.passwordHint': 'At least 10 characters.',
  'auth.forgot': 'Forgot password?',
  'auth.forgotSent': 'If that account exists, we have sent you an email.',
  'auth.busy': 'Checking…',
  'auth.carriesOver': 'What carries over',
  'auth.carriesWatchlist': 'Your saved films',
  'auth.carriesRatings': 'Your ratings',
  'auth.carriesFilms': 'Films you suggested',
  'auth.carriesNote': 'This only applies to this device.',
  'auth.errorCredentials': 'Email, username, or password is wrong.',
  'auth.errorDuplicate': 'There is already an account for this email.',
  'auth.errorDuplicateUsername': 'That username is already taken.',
  'auth.errorPasswordShort': 'The password needs at least 10 characters.',
  'auth.errorEmailInvalid': 'That doesn’t look like a valid email address.',
  'auth.errorUsernameInvalid': 'Usernames are 3–32 characters: letters, numbers, "_" and "-".',
  'auth.errorGeneric': 'Something went wrong.',
  'auth.errorTooMany': 'Too many attempts. Please try again later.',

  // Account: what an account is worth, and the ledger behind it.
  'account.title': 'Account',
  'account.confirmed': 'Email confirmed',
  'account.unconfirmed': 'Email not confirmed yet',
  'account.adFreeUntil': 'Ad-free until {date}',
  'account.adFreeNone': 'No ad-free credit yet.',
  'account.daysLeft': '{days} days left',
  'account.perContribution': 'Confirmed contribution: 3 days. Filled gap: 6 days.',
  'account.cap': 'Capped at 90 days at a time.',
  'account.confirmedContributions': 'Confirmed contributions',
  'account.openContributions': 'Open contributions',
  'account.payoutNote':
    'Credit arrives only once a fact is settled — and only for the answer that wins. Not for taking part.',
  'account.ledger': 'History',
  'account.ledgerEmpty': 'No entries yet.',
  'account.logout': 'Log out',
  'account.reasonFill': 'Filled a gap',
  'account.reasonConfirm': 'Contribution confirmed',
  'account.seenSettingsTitle': 'Already-seen films',
  'account.seenSettingsHint':
    'When you mark a film already seen while swiping, this is how long it stays out of your deck in a session.',
  'account.seenExclusionDays': '{days} days',
  'account.seenExclusionForever': 'Forever',
  'account.preferencesTitle': 'Defaults for new sessions',
  'account.preferencesHint':
    'Pick the streaming services you have and the genres you like — a new session starts with these already selected.',
  'account.languageHint':
    'FilmSloth started in your browser’s language. Change it here if that guess was wrong.',

  'account.statsTitle': 'Usage',
  'account.statsHint':
    'How much the site is being used — page loads, rounds, and whether people arrive through the installed app or a browser. Moderators only.',
  'account.statsOpen': 'Open the usage numbers',

  // The operator's own instrument panel. Counter names are shown exactly as
  // they are stored rather than translated — see StatsScreen.tsx for why.
  'stats.title': 'Usage',
  'stats.intro':
    'Aggregate counters: one tally per day, with nothing in them that says who anybody is. Actions are counted by the server once they succeed; page loads are reported by the page itself and can be blocked, so read those as a floor rather than a measurement.',
  'stats.range': 'Period',
  'stats.rangeDays': 'Last {days} days',
  'stats.forbidden': 'These numbers are for moderators, and this account is not one.',
  'stats.failed': 'The numbers could not be loaded.',
  'tmdb.title': 'Film catalogue (TMDB)',
  'tmdb.reload': 'Reload',
  'tmdb.loadFailed': 'The sync status could not be loaded.',
  'tmdb.disabled':
    'TMDB is not set up on the server (TMDB_READ_TOKEN is missing). The deck deals from the shipped seed.',
  'tmdb.running': 'A sync is running…',
  'tmdb.fallback':
    'The TMDB catalogue is empty, so the deck currently deals from the shipped seed.',
  'tmdb.films': 'Films in the deck',
  'tmdb.due': 'Due for a refresh',
  'tmdb.withoutDetails': '{count} never fetched',
  'tmdb.oldest': 'Oldest data in the deck',
  'tmdb.next': 'Next scheduled run',
  'tmdb.settings':
    'Daily: up to {perRun} films refreshed, {pages} discover pages per service. Data counts as stale after {days} days; checked every {hours} h.',
  'tmdb.runsTitle': 'Recent runs',
  'tmdb.colStarted': 'Started',
  'tmdb.colStatus': 'Status',
  'tmdb.colTrigger': 'Started by',
  'tmdb.colDuration': 'Took',
  'tmdb.colDiscovered': 'Found',
  'tmdb.colRefreshed': 'Refreshed / due',
  'tmdb.colGone': 'Gone',
  'tmdb.noRuns': 'No runs yet.',
  'tmdb.status.ok': 'OK',
  'tmdb.status.failed': 'Failed',
  'tmdb.status.running': 'Running',
  'tmdb.trigger.scheduled': 'Schedule',
  'tmdb.trigger.manual': 'Manual',
  'tmdb.refreshedOf': '{done} / {due}',
  'tmdb.manualTitle': 'Start a sync now',
  'tmdb.manualHint':
    'Refreshes up to the given number of films, never-fetched ones first, then the stalest. A manual run does not move the daily schedule.',
  'tmdb.detailsLimit': 'Most films to refresh',
  'tmdb.discover': 'Look for new films first (discover)',
  'tmdb.discoverPages': 'Discover pages per service (0 = all)',
  'tmdb.start': 'Start sync',
  'tmdb.alreadyRunning': 'A sync is already running.',
  'tmdb.startFailed': 'The sync could not be started.',
  'tmdb.invalidLimit': 'Enter a whole number between 0 and {max}.',
  'stats.recordingOff':
    'Counting is switched off (USAGE_STATS=off). The standing numbers further down are still true; the counters have simply stopped moving.',
  'stats.visits': 'Page loads in this period',
  'stats.todayValue': 'Today: {count}',
  'stats.percent': '{value}%',
  'stats.appShare': 'Loads from the installed app',
  'stats.appVersusWeb': 'App {app} · browser {web}',
  'stats.sessionsCreated': 'Rounds created',
  'stats.startedValue': 'Of those, started: {count}',
  'stats.filmsChosen': 'Films a group settled on',
  'stats.matchesValue': 'Matches: {count}',
  'stats.newDevices': 'New devices',
  'stats.newDevicesHint': 'Browsers that got a device identifier for the first time.',
  'stats.newAccounts': 'New accounts',
  'stats.accountsTotal': '{count} in total',
  'stats.perDayTitle': 'Day by day',
  'stats.perDayHint':
    'The bar behind each date is that day’s page loads, relative to the busiest day listed. A dot means nothing was counted.',
  'stats.day': 'Day',
  'stats.countersTitle': 'Every counter',
  'stats.countersHint':
    'Names as stored. “?” is a request whose client never said which shell it came from — a crawler, a script, or a page cached from before this shipped.',
  'stats.counter': 'Counter',
  'stats.total': 'Period',
  'stats.today': 'Today',
  'stats.empty': 'Nothing has been counted in this period yet.',
  'stats.standingTitle': 'Standing numbers',
  'stats.standingHint':
    'Counted out of the tables themselves, so these cover the whole history of the deployment rather than the period above.',
  'stats.standingDevices': 'Devices known',
  'stats.standingAccounts': 'Accounts, confirmed of total',
  'stats.standingFilms': 'Films, community of total',
  'stats.standingClaims': 'Fact contributions',
  'stats.standingWatchlist': 'Watchlist entries',
  'stats.standingRatings': 'Ratings',
  'stats.standingLiveSessions': 'Rounds alive right now',
  'stats.confirmedOf': '{confirmed} of {total}',
  'stats.communityOf': '{community} of {total}',
  'stats.footer': '{from} to {to}, days counted in UTC. Read at {time}.',

  'ads.label': 'Advertisement',
  'ads.placeholder': 'An ad would show here.',

  // Workshop: one open fact at a time — fill it, confirm it, settle a
  // dispute, or recheck a streaming fact old enough to doubt.
  'workshop.title': 'Workshop',
  'workshop.subtitle': 'Check and confirm facts about films — one at a time.',
  'workshop.empty': 'Nothing to settle right now.',
  'workshop.emptyHint': 'Check back later when new facts are open.',
  'workshop.openPoints': '{count} open points',
  'workshop.unlockable': '{count} films almost deck-ready',
  'workshop.contributionsConfirmed': '{count} confirmed contributions',
  'workshop.contributionsOpen': '{count} open contributions',
  'workshop.skip': 'Skip',
  'workshop.openRecord': 'Open film record',
  'workshop.loginRequired': 'You need an account to contribute.',
  'workshop.loginCta': 'Sign in or register',
  'workshop.position': '{current} of {total}',
  'workshop.typeFill': 'Fill in',
  'workshop.typeConfirm': 'Confirm',
  'workshop.typeDecide': 'Decide',
  'workshop.typeRefresh': 'Refresh',
  // Context line under a work item's title, assembled from the counts the
  // server sends: "1966 · 161 min · 2 genres · 1 service".
  'workshop.summaryEmpty': 'Nothing confirmed yet',
  'workshop.summaryGenre': '1 genre',
  'workshop.summaryGenres': '{count} genres',
  'workshop.summaryProvider': '1 streaming service',
  'workshop.summaryProviders': '{count} streaming services',
  'workshop.fieldYear': 'Year',
  'workshop.fieldRuntime': 'Runtime',
  'workshop.fieldGenre': 'Genre',
  'workshop.fieldProvider': 'Streaming service',
  'workshop.askYear': 'Is the year right?',
  'workshop.askRuntime': 'Is the runtime right?',
  'workshop.askGenre': 'Does the genre {genre} fit?',
  'workshop.askProvider': 'Does it stream on {provider}?',
  'workshop.askRefresh': 'Does it still stream here?',
  'workshop.yes': 'Yes',
  'workshop.no': 'No',
  'workshop.correct': 'Correct',
  'workshop.incorrectWithValue': 'Wrong — correct value:',
  'workshop.unsure': 'Not sure',
  'workshop.submit': 'Submit',
  'workshop.enterValue': 'Enter a value',
  'workshop.noNeedsAlternative': 'A bare no helps nobody. Tell us what is right.',
  'workshop.oneSupporter': 'Only one vote so far.',
  'workshop.supportersNeeded': '{needed} more needed',
  'workshop.stillValid': 'Still current',
  'workshop.lastChecked': 'Last checked {days} days ago',
  'workshop.unlocksFilm': 'This makes the film deck-ready.',
  'workshop.creditEarned': '+{days} ad-free days',
  'workshop.stillRuns': 'Still streams here',
  'workshop.gone': 'No longer here',

  // Film record: a single film's page, one row per fact.
  'record.eyebrow': 'Film record',
  'record.deckReady': 'Deck-ready',
  'record.notDeckReady': 'Not deck-ready yet',
  'record.provisional': 'Availability unchecked',
  'record.openPoints': '{count} open points',
  'record.addGenre': 'Add a genre',
  'record.addProvider': 'Add a streaming service',
  'record.stateConfirmed': 'Confirmed',
  'record.stateProposed': 'Proposed',
  'record.stateDisputed': 'Disputed',
  'record.stateStale': 'Stale',
  'record.lockedBy': 'Set by {name}',
  'record.lockedAnonymous': 'Set by a moderator',
  'record.lock': 'Freeze',
  'record.unlock': 'Release',
  'record.reopen': 'Ask again',
  'record.stateOpen': 'Open',
  'record.votes': '{count} votes',
  'record.youAnsweredYes': 'You said yes. Waiting for a second confirmation.',
  'record.youAnsweredNo': 'You said no. Waiting for a second confirmation.',
  'record.youAnsweredValue': 'You said {value}. Waiting for a second confirmation.',
  'record.year': 'Year',
  'record.runtime': 'Runtime',
  'record.genres': 'Genres',
  'record.providers': 'Streaming services',
  'record.trailer': 'Trailer',
  'record.trailerNotYet': 'A trailer can be added once this film is deck-ready.',
  'record.trailerAddSuccess': 'Thanks! The trailer is now live for everyone.',
  'record.trailerRemoved': 'Removed for everyone. Somebody can now add the right one.',
  'record.trailerReviewOnly':
    'Noted — this trailer was added by us directly, so removing it needs a person to review it.',
  'record.trailerCounted': 'Noted. If a few more people say the same, it comes down for everyone.',
  'record.trailerReportFailed': 'That did not go through. Please try again in a little while.',
  'record.blocked': 'Blocked until these are settled: {missing}.',
  'record.ready': 'Ready for the deck.',

  // Adding a film: only the title is required. Everything else becomes an
  // open point for the workshop.
  'film.addTitle': 'Add a film',
  'film.addHint':
    'Enter the title. The workshop sorts out the year, runtime, genres and providers afterwards.',
  'film.titleLabel': 'Title',
  'film.titleRequired': 'The title is the only thing you need to enter.',
  'film.optional': 'Optional',
  'film.optionalHint': 'Everything else is optional — the workshop settles it over time.',
  'film.year': 'Year',
  'film.runtime': 'Runtime',
  'film.genres': 'Genres',
  'film.providers': 'Streaming services',
  'film.submit': 'Submit',
  'film.submitting': 'Submitting…',
  'film.success': 'Film added.',
  'film.duplicate': 'This film already exists.',
  'film.footnote': 'You do not need an account for this.',
  'film.cancel': 'Cancel',

  // Suggesting a film during swiping: search first, so nobody duplicates a
  // title that is already in the database, and the winner is voted on next.
  'film.suggestTitle': 'Suggest a film',
  'film.suggestHint': 'Search the catalogue and put a film in front of the whole group.',
  'film.searching': 'Searching…',
  'film.suggestAction': 'Suggest',
  'film.suggestPending': 'Already suggested — waiting on the community to confirm it.',
  'film.suggestNoResults':
    'Nothing found. Only films streaming on a supported service are in the catalogue.',
  'film.suggestAddNew': 'Add it',
  'film.suggestNotListed': 'Not the one you mean?',
  'film.suggestAddNewTitled': 'Add "{title}" as a new film',
  'film.suggestCreateHint':
    'The group votes on this next. The title is all it takes — everything else can be filled in later through the Filmwerkstatt.',
  'film.suggestCreateRequired': 'A title is required.',
  'film.suggestBackToSearch': 'Back to search',
  'film.suggestSuccess': 'Suggested! It is next up for everyone.',

  // Why a suggestion was refused. The deck holds every title the filters
  // allow, so "it is already in the deck" is not one of these: that film is
  // simply moved to the front.
  'suggestError.already_voted': 'Everybody here has already voted on that film.',
  'suggestError.not_swiping': 'The session is not taking votes right now.',
  'suggestError.film_not_found': 'That film is not in the database any more.',
  'suggestError.unknown': 'That did not work. Please try again.',

  // Home screen teasers for the workshop and the account.
  'home.workshop': 'Workshop',
  'home.workshopHint': '{count} open points waiting',
  'home.workshopLocked': 'Contributing needs an account.',
  'home.workshopOpen': 'Open the workshop',
  'home.workshopSignIn': 'Sign in and join in',
  'home.signIn': 'Sign in',
  'home.adFreeActive': 'Ad-free until {date}',
  'home.deviceSettings': 'App settings',

  // Adding the app to a home screen.
  'install.title': 'Put it on your home screen',
  'install.body': 'Opens without the browser bar, and the code is one tap away next time.',
  'install.iosBody': 'Tap the share button, then “Add to Home Screen”.',
  'install.action': 'Install',
  'install.dismiss': 'Not now',
  'device.install': 'Add to home screen',
  'device.installHint': 'Opens without the browser bar.',
  'device.installed': 'Already installed on this device.',

  // Settings that belong to this phone rather than to an account.
  'device.title': 'On this device',
  'device.intro':
    'These are answers about this phone or tablet. They are kept here, never on your account, so a different device can answer differently.',
  'device.haptics': 'Vibration',
  'device.hapticsHint': 'A short buzz when you vote, a longer one when the group matches.',
  'device.hapticsUnsupported': 'This browser cannot vibrate. On iPhone and iPad no browser can.',
  'device.keepAwake': 'Keep the screen on',
  'device.keepAwakeHint':
    'While a session is running, so the screen does not go dark between two cards.',
  'device.keepAwakeUnsupported':
    'This browser cannot hold the screen on. Your usual screen timeout applies.',
  'device.motionTitle': 'Motion',
  'device.motionReduced':
    'Your system asks for less motion, so animations are off. Change it in your system settings.',
  'device.motionFull': 'Animations follow your system setting. Nothing to set here.',

  // Legal notice (Impressum), required by German law (§ 5 DDG).
  'legal.lead': 'Who runs this website, how to reach them, and the details § 5 DDG asks for.',
  'legal.title': 'Legal notice',
  'legal.responsibleFor': 'Responsible for the content of this website according to § 5 DDG:',
  'legal.contact': 'Contact',
  'legal.email': 'Email',

  // Site-wide navigation. These four pages have addresses of their own (see
  // lib/routes.ts) and link to each other from the footer of every page.
  'nav.label': 'Site navigation',
  'nav.home': 'Home',
  'nav.about': 'About us',
  'nav.contact': 'Contact',
  'nav.privacy': 'Privacy',
  'nav.legal': 'Legal notice',
  'nav.attribution': 'Film data: TMDB · Streaming data: JustWatch',

  // What search engines and shared links show. Content pages build their own
  // title from `meta.pageTitle` and use their opening line as the description.
  'meta.title': 'FilmSloth — decide together what to watch tonight',
  'meta.description':
    'FilmSloth is a free group picker for film night: everybody swipes through the same deck, one No takes a film out, and the first title nobody objects to wins.',
  'meta.pageTitle': '{page} — FilmSloth',

  // About us. Required reading for anyone deciding whether to trust the site,
  // and the page most often missing from small sites.
  'about.title': 'About us',
  'about.lead':
    'FilmSloth is a small, independent site that helps a group of friends agree on a film in minutes instead of half an evening.',

  'about.whatTitle': 'What the site does',
  'about.whatBody1':
    'One person opens a session, picks the streaming services everybody has and shares a six-character code. Everyone then swipes through the same shuffled deck on their own phone and answers Yes, Don’t mind or No. A No takes a film out for the whole group, a Yes or a Don’t mind keeps it in play. The first title nobody has objected to is the match, and the group either watches it or keeps looking.',
  'about.whatBody2':
    'There is nothing to install and no account is needed to join a round. Everything happens in the browser, on phones, tablets and desktops alike.',

  'about.whoTitle': 'Who is behind it',
  'about.whoBody1':
    'FilmSloth is run by one person, Johannes Mahl, from Frankfurt am Main in Germany. It started as a fix for an argument that kept repeating itself — four people, three streaming services, twenty minutes of scrolling and still no film — and grew into the site you are reading about.',
  'about.whoBody2':
    'It belongs to no studio, streaming service or publisher, and nobody can pay to have a film ranked higher in a deck. The full name and postal address behind the site are in the legal notice.',

  'about.dataTitle': 'Where the film data comes from',
  'about.dataBody1':
    'Every film in the deck comes from The Movie Database (TMDB), a database of films maintained by its community: title, year, runtime, genres, description, poster and trailer. Which streaming services a film is on in Germany comes from JustWatch, passed on by TMDB. FilmSloth refreshes all of it automatically every day; nobody types it in by hand any more.',
  'about.dataBody2':
    'Streaming catalogues change daily, so now and then a film may have just left a service. If something looks wrong, tell us — or correct it at the source on themoviedb.org, where it will reach every site that uses the data.',

  'about.moneyTitle': 'How the site is paid for',
  'about.moneyBody1':
    'FilmSloth is free and shows no advertising. Running it still costs money — server, domain, database — and that is paid by the person behind the site and by voluntary donations.',
  'about.moneyBody2':
    'Nobody can pay to move a film further up the deck, and no data about you is sold or shared with advertisers.',
  'about.creditsTitle': 'Film data and credits',
  'about.creditsTmdb': 'This product uses the TMDB API but is not endorsed or certified by TMDB.',
  'about.creditsTmdbBody':
    'Film titles, descriptions, posters, trailers, ratings and release data are provided by TMDB (themoviedb.org) under its API terms. FilmSloth is a non-commercial project.',
  'about.creditsJustWatch': 'Streaming availability data is provided by JustWatch.',
  'about.creditsTmdbLink': 'The Movie Database (TMDB)',
  'about.creditsJustWatchLink': 'JustWatch',

  'about.contactTitle': 'Getting in touch',
  'about.contactBody':
    'Questions, corrections, bug reports and press enquiries are all welcome. The contact page has the address and says what to expect.',

  // Contact. The second page AdSense looks for, and the one that decides
  // whether a wrong film fact ever reaches us.
  'contact.title': 'Contact',
  'contact.lead':
    'One address reaches the person who runs FilmSloth — for questions, corrections, bug reports and anything legal.',

  'contact.emailTitle': 'By email',
  'contact.emailBody':
    'Email is the fastest way to reach us and the only channel we watch daily. Please write in German or English.',

  'contact.topicsTitle': 'What to write about',
  'contact.topicBug':
    'Something is broken: what you did, on which device, and what happened instead.',
  'contact.topicFilm':
    'A film entry is wrong, a trailer points at the wrong video, or a title is missing entirely.',
  'contact.topicAccount':
    'Trouble signing in, a confirmation mail that never arrived, or a deletion request.',
  'contact.topicPrivacy':
    'Data protection: access to your data, correction, deletion, or an objection to how it is used.',
  'contact.topicAds': 'Press and anything else about the site as a whole.',

  'contact.responseTitle': 'When to expect an answer',
  'contact.responseBody':
    'FilmSloth is run by one person alongside a day job. Most mail is answered within a few days; anything about data protection is handled within the one month the GDPR allows, and usually much sooner.',

  'contact.postalTitle': 'By post',
  'contact.postalBody':
    'Post reaches us at the address below, which is also the one in the legal notice.',

  // Privacy. German law asks for this, and so do the AdSense programme
  // policies: a site running ads has to say what the ads do with visitors.
  'privacy.title': 'Privacy',
  'privacy.lead':
    'What FilmSloth stores about you, why it stores it, who else sees it, and how to get rid of it.',
  'privacy.updated': 'Last updated: {date}',

  'privacy.controllerTitle': 'Who is responsible',
  'privacy.controllerBody':
    'The controller for the processing described here, in the sense of the GDPR, is:',

  'privacy.hostingTitle': 'Visiting the site',
  'privacy.hostingBody1':
    'The site runs on servers of Render Services, Inc. in Frankfurt am Main. Opening a page means your browser sends an address, a request time, the page requested and a browser identification; the hosting provider keeps that for a short period to run and secure the service. The legal basis is our legitimate interest in a working, attack-resistant site (Art. 6(1)(f) GDPR).',
  'privacy.hostingBody2':
    'We do not use third-party analytics. No Google Analytics, no tracking pixel, and nothing about your visit is passed on to another company — film posters are loaded from TMDB by our server and handed on to you, so your browser never contacts TMDB. What FilmSloth counts itself is described in full in the next section.',

  // Our own counting. The section exists because the sentence above used to
  // read "we do not run analytics" flatly, and that stopped being true the
  // day the counters shipped. What it describes is `UsageCounter` in
  // schema.prisma and `lib/usage.ts` in the client, and nothing more.
  'privacy.usageTitle': 'Counting how much the site is used',
  'privacy.usageBody1':
    'FilmSloth keeps a tally of how busy it is, on its own server. It counts things like “a page was opened”, “a round was created”, “a group settled on a film”, and whether the page was opened in the installed app or in a browser tab. What is stored for each of those is a date, the name of the thing and a number — and nothing else. There is no record of your visit, no address, no browser identification, no order of pages; nothing in these numbers can be traced back to you, and there is nothing in them to build a profile out of.',
  'privacy.usageBody2':
    'Nothing is stored on your device or read from it for this. The counting sets no cookie and reads no identifier, so § 25 TMDDG — the rule that asks for consent before anything is put on or taken from your device — does not come into play. The legal basis is our legitimate interest in knowing whether the site is used at all (Art. 6(1)(f) GDPR). So that nobody can simply inflate the numbers, the server briefly keeps a count per internet address in memory, for an hour; it is never written down and never stored with the tallies.',
  'privacy.usageBody3':
    'The numbers are for us. They are shown on one page that requires a moderator account, and they are neither passed on nor sold.',

  'privacy.storageTitle': 'Cookies and local storage',
  'privacy.storageBody':
    'FilmSloth sets no advertising cookies of its own. What it does store is what makes the site work at all:',
  'privacy.storageSession':
    'A cookie called "sessionId": an anonymous, random device id, so your watchlist and ratings stay yours without an account. It expires after 30 days.',
  'privacy.storageLogin':
    'An “auth” cookie, only once you sign in: it is what keeps you signed in, and it disappears when you sign out.',
  'privacy.storageLocale':
    'Your language choice and your answer to the trailer question, in your browser’s local storage, so neither has to be asked again.',
  'privacy.storageRound':
    'The code and token of the round you are in, in local storage, so reloading the page does not throw you out of it.',
  'privacy.storageBody2':
    'The cookies above are strictly necessary for the functions you asked for (§ 25(2) TMDDG, Art. 6(1)(f) GDPR). Clearing your browser data removes all of them; the site then treats you as a first-time visitor.',

  'privacy.accountTitle': 'If you create an account',
  'privacy.accountBody1':
    'An account consists of a username, an email address and your password as an irreversible hash, plus the films you rated, saved or marked as seen. The email address exists so the account can be confirmed and recovered; it is never sold, rented or passed on. The legal basis is the contract you enter into by registering (Art. 6(1)(b) GDPR).',
  'privacy.accountBody2':
    'On request we delete the account and everything personal attached to it.',

  'privacy.adsTitle': 'Advertising',
  'privacy.adsBody1':
    'FilmSloth shows ads through Google AdSense, a service of Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Ireland. Google uses cookies and comparable identifiers to show ads, to measure whether they were seen, and — depending on your settings and your consent — to tailor them to you. That can mean data leaving the EU; Google covers those transfers with the European Commission’s standard contractual clauses.',
  'privacy.adsBody2':
    'You decide what Google may use. Personalised advertising can be switched off at any time in Google’s ad settings, and the consent you gave for this site can be changed or withdrawn there as well. Ads do not disappear when you do that — they just stop being personalised.',
  'privacy.adsBody3':
    'Contributing to the workshop earns ad-free days, during which no ad is requested at all.',
  'privacy.adsSettingsLink': 'Google ad settings',
  'privacy.adsPolicyLink': 'How Google uses data from ads',

  'privacy.trailersTitle': 'Trailers from YouTube',
  'privacy.trailersBody':
    'Trailers play in YouTube’s own player, and nothing at all is requested from Google until you press play the first time — not the video, not even its thumbnail. Once you do, YouTube receives your address and can set cookies; we use the privacy-enhanced youtube-nocookie.com host to keep that to a minimum. Your answer is remembered in your browser and can be taken back on any film page.',
  'privacy.tmdbTitle': 'Film data from TMDB',
  'privacy.tmdbBody':
    'Film information and posters come from The Movie Database (TMDB, TMDB Inc., USA), streaming availability from JustWatch via TMDB. Our server fetches this data and the poster images itself and passes them on to you. Your browser does not connect to TMDB or JustWatch, and no data about you is sent to either.',

  'privacy.rightsTitle': 'Your rights',
  'privacy.rightsBody':
    'You can ask us, free of charge, for any of the following. A short mail to the contact address is enough:',
  'privacy.rightsAccess': 'A copy of the data we hold about you, and what we do with it.',
  'privacy.rightsRectify': 'Correction of anything that is wrong.',
  'privacy.rightsErase': 'Deletion of your account and the personal data attached to it.',
  'privacy.rightsRestrict': 'Restriction of processing, or an objection to it.',
  'privacy.rightsPortability': 'Your data in a portable, machine-readable form.',
  'privacy.rightsComplain':
    'A complaint to a supervisory authority — for us that is the Hessischer Beauftragter für Datenschutz und Informationsfreiheit — if you think we got this wrong.',

  'privacy.retentionTitle': 'How long anything is kept',
  'privacy.retentionBody':
    'A round and its deck are deleted a few hours after the last person leaves. Account data stays until you delete the account. Technical logs are kept only as long as the hosting provider needs them to run and protect the service.',

  'privacy.changesTitle': 'Changes to this page',
  'privacy.changesBody':
    'When the site changes what it does with data, this page changes with it. The date above says when it last did.',

  // Genre names. The ids live in `shared`; only the wording is per locale.
  'genre.action': 'Action',
  'genre.adventure': 'Adventure',
  'genre.animation': 'Animation',
  'genre.comedy': 'Comedy',
  'genre.crime': 'Crime',
  'genre.documentary': 'Documentary',
  'genre.drama': 'Drama',
  'genre.family': 'Family',
  'genre.fantasy': 'Fantasy',
  'genre.history': 'History',
  'genre.horror': 'Horror',
  'genre.music': 'Music',
  'genre.mystery': 'Mystery',
  'genre.romance': 'Romance',
  'genre.science-fiction': 'Science Fiction',
  'genre.thriller': 'Thriller',
  'genre.war': 'War',
  'genre.western': 'Western',
} as const;

export type TranslationKey = keyof typeof en;
